"use client";

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { requireAiHqUiMutationApproval } from "@/lib/virtualGate/aiHqUiMutationGate";

export type AiHqMessage = { role: "user" | "assistant" | "error"; content: string; imageName?: string };

type AiHqContextValue = {
  messages: AiHqMessage[];
  sending: boolean;
  elapsedMs: number;
  predictedMs: number | null;
  sendText: (message: string, image?: File | null) => Promise<void>;
  cancel: () => Promise<void>;
  reset: () => Promise<void>;
};

const AiHqContext = createContext<AiHqContextValue | null>(null);

function requireAiHqProviderMutationConnection(): never {
  throw new Error("RUNTIME_BLOCKED: AI-HQ provider mutation resolver is not connected");
}

/** AI本部（財務）チャットの状態・送信処理を返す。RootLayoutに置いた<AiHqProvider>配下でのみ使える。 */
export function useAiHq(): AiHqContextValue {
  const ctx = useContext(AiHqContext);
  if (!ctx) throw new Error("useAiHq must be used within AiHqProvider");
  return ctx;
}

/** ブラウザ通知（他の画面に移動していても届く）。許可されていなければ何もしない。 */
function notify(title: string, body: string): void {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    new Notification(title, { body });
  } catch {
    // 通知に失敗しても致命的ではない
  }
}

/**
 * AI本部（財務）チャットの状態をRootLayout直下に置き、ページ遷移をまたいで維持する。
 * これまでは`AiHqChat`コンポーネント内のローカル状態だったため、送信中に他の画面へ移動すると
 * コンポーネントごと消え、「止まっているように見える」問題があった。実際の処理（サーバー側の
 * claude呼び出し・DB保存）は元々止まっていなかったが、進捗表示・完了の把握ができなかった。
 */
export function AiHqProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<AiHqMessage[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [predictedMs, setPredictedMs] = useState<number | null>(null);
  const sendingRef = useRef(sending);
  sendingRef.current = sending;

  // 送信中は経過時間を表示するため、100msごとに更新する。
  useEffect(() => {
    if (!sending) return;
    const startedAt = Date.now();
    setElapsedMs(0);
    const timer = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(timer);
  }, [sending]);

  // 画面遷移で戻ってきても会話・予測時間の目安が消えないよう、DBから読み込む。
  useEffect(() => {
    if (historyLoaded) return;
    fetch("/api/ai-hq")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data.messages)) {
          setMessages(data.messages.map((m: { role: string; content: string; imageName?: string | null }) => ({
            role: m.role as AiHqMessage["role"],
            content: m.content,
            imageName: m.imageName ?? undefined,
          })));
        }
        if (typeof data.avgDurationMs === "number") setPredictedMs(data.avgDurationMs);
      })
      .catch(() => {})
      .finally(() => setHistoryLoaded(true));
  }, [historyLoaded]);

  async function sendText(message: string, image?: File | null) {
    requireAiHqUiMutationApproval();
    if (!message || sendingRef.current) return;
    requireAiHqProviderMutationConnection();
    throw new Error("RUNTIME_BLOCKED: AI-HQ provider mutation resolver is not connected");

    // 通知の許可はユーザー操作（送信）の直後に確認する（ブラウザの制約でジェスチャー起点が必要）。
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    setMessages((prev) => [...prev, { role: "user", content: message, imageName: image?.name }]);
    setSending(true);

    try {
      const form = new FormData();
      form.append("message", message);
      const imageValue = image;
      if (imageValue !== undefined && imageValue !== null) form.append("image", imageValue as Blob);
      const res = await fetch("/api/ai-hq", { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok) {
        const errText = data.error ?? "呼び出しに失敗しました。";
        setMessages((prev) => [...prev, { role: "error", content: errText }]);
        // 自分でキャンセルした場合は通知しない（意図した操作のため）。
        if (errText !== "キャンセルされました。") notify("AI本部（財務）", errText);
      } else {
        const reply = data.reply || "（応答なし）";
        setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
        if (typeof data.avgDurationMs === "number") setPredictedMs(data.avgDurationMs);
        notify("AI本部（財務）から返信が届きました", reply.slice(0, 120));
      }
    } catch {
      const errText = "サーバーに接続できませんでした。";
      setMessages((prev) => [...prev, { role: "error", content: errText }]);
      notify("AI本部（財務）", errText);
    } finally {
      setSending(false);
    }
  }

  /** 応答待ちのターンだけをキャンセルする（会話は継続。sendingがfalseに戻るのはPOST側のcatchで処理される）。 */
  async function cancel() {
    requireAiHqUiMutationApproval();
    if (!sendingRef.current) return;
    requireAiHqProviderMutationConnection();
    throw new Error("RUNTIME_BLOCKED: AI-HQ provider mutation resolver is not connected");
    try {
      await fetch("/api/ai-hq", { method: "PATCH" });
    } catch {
      // 失敗しても致命的ではない（元の送信はどのみちタイムアウトかエラーで終わる）
    }
  }

  async function reset() {
    requireAiHqUiMutationApproval();
    throw new Error("RUNTIME_BLOCKED: AI-HQ provider mutation resolver is not connected");
    requireAiHqProviderMutationConnection();
    throw new Error("RUNTIME_BLOCKED: AI-HQ provider mutation resolver is not connected");
    setSending(true);
    try {
      await fetch("/api/ai-hq", { method: "DELETE" });
      setMessages([]);
    } finally {
      setSending(false);
    }
  }

  return (
    <AiHqContext.Provider value={{ messages, sending, elapsedMs, predictedMs, sendText, cancel, reset }}>
      {children}
    </AiHqContext.Provider>
  );
}
